import { Box, Chip, List, ListItem, Popover, Typography } from '@mui/material';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useEdges, useNodes } from 'reactflow';
import 'reactflow/dist/style.css';
import { isDesignCanvasProject } from '../../config/designMode';
import useApi from '../../hooks/useAPI';
import { formatLastRunLabel } from '../Canvas/utils/workflowRun';
import NodeHandles from './components/NodeHandles';
import NodeHeader from './components/NodeHeader';
import useConnectionEffect from './hooks/useConnectionEffect';
import useNodeData from './hooks/useNodeData';
import useNodeDeployment from './hooks/useNodeDeployment';
import useRobustWebSocket from './hooks/useRobustWebSocket';
import { NodeContainer, StatusContainer } from './styles';

const ProjectPackage = memo(({ id, data, isConnectable }) => {
  const { isTemp, inputs, outputs } = data;
  const { projectId } = useParams();
  const [nodeData, updateNodeData] = useNodeData(data);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const canvasPatchRef = useRef(data.updateNodeData);
  useEffect(() => {
    canvasPatchRef.current = data.updateNodeData;
  }, [data.updateNodeData]);

  const syncLocalAndCanvas = useCallback(
    (updater) => {
      updateNodeData((prev) => {
        const next =
          typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
        const patchCanvas = canvasPatchRef.current;
        if (typeof patchCanvas === 'function') {
          const patch =
            typeof updater === 'function'
              ? Object.fromEntries(
                  Object.keys(next)
                    .filter((k) => next[k] !== prev[k])
                    .map((k) => [k, next[k]])
                )
              : updater;
          if (patch && Object.keys(patch).length > 0) {
            // Never call Flow setNodes from inside useNodeData's setState updater —
            // that updates Canvas while the node is still rendering.
            queueMicrotask(() => {
              canvasPatchRef.current?.(patch);
            });
          }
        }
        return next;
      });
    },
    [updateNodeData]
  );

  const syncCanvasToFlow = useCallback((patch) => {
    canvasPatchRef.current?.(patch);
  }, []);

  const {
    error: localDeployError,
    handleDeploy: localHandleDeploy,
    handleDestroy: localHandleDestroy,
    handleRun: localHandleRun,
  } = useNodeDeployment(nodeData.id, updateNodeData, syncCanvasToFlow);
  const handleDeploy = data.onDeploy || localHandleDeploy;
  const handleDestroy = data.onDestroy || localHandleDestroy;
  const handleRun = data.onRun || localHandleRun;
  const handleRunNow = data.onRunNow;
  const handleActivate = data.onActivate;
  const handleDeactivate = data.onDeactivate;
  const error = localDeployError;
  const {
    sendJsonMessage,
    connectionStatus,
    error: wsError,
  } = useRobustWebSocket(projectId, nodeData.id, syncLocalAndCanvas);

  const canvasEdges = useEdges();
  const canvasNodes = useNodes();
  const destroyBlockedByDownstream = useMemo(() => {
    if (isDesignCanvasProject(projectId)) return false;
    const id = nodeData.id;
    if (!id) return false;
    return canvasEdges.some((edge) => {
      if (edge.source !== id) return false;
      const target = canvasNodes.find((n) => n.id === edge.target);
      const deployStatus =
        target?.data?.deploy_status ?? 'NOT_DEPLOYED';
      return deployStatus !== 'NOT_DEPLOYED';
    });
  }, [canvasEdges, canvasNodes, nodeData.id, projectId]);

  const typeLine = useMemo(() => {
    const bits = [nodeData.type];
    if (nodeData.type === 'agent/think') {
      const role = nodeData.parameter_data?.role;
      const model = nodeData.parameter_data?.model;
      if (role) bits.push(role);
      if (model) bits.push(model);
    }
    return bits.filter(Boolean).join(' · ');
  }, [nodeData.type, nodeData.parameter_data]);

  const handlePreview =
    nodeData.output_data?.thought?.preview ||
    nodeData.output_data?.handle?.preview ||
    nodeData.output_data?.tick?.preview ||
    nodeData.output_data?.result?.preview ||
    nodeData.output_data?.combined?.preview ||
    nodeData.output_data?.resource?.preview ||
    '';
  const tokenCount = nodeData.output_data?.usage?.total_tokens;
  const lastRunLabel = formatLastRunLabel(nodeData.output_data?.last_run);
  const api = useApi();
  const [jobsAnchor, setJobsAnchor] = useState(null);
  const [nodeJobs, setNodeJobs] = useState([]);
  const jobsOpen = Boolean(jobsAnchor);
  const openJobs = async (event) => {
    setJobsAnchor(event.currentTarget);
    try {
      const res = await api.listPackageJobs(projectId, id);
      setNodeJobs(Array.isArray(res) ? res : res?.jobs || []);
    } catch {
      setNodeJobs([]);
    }
  };
  const closeJobs = () => setJobsAnchor(null);
  const timerArmed = Boolean(nodeData.parameter_data?.active);

  const hasAgentGraph = useMemo(
    () =>
      canvasNodes.some((n) =>
        String(n?.data?.type || '').startsWith('agent/')
      ),
    [canvasNodes]
  );

  useConnectionEffect(connectionStatus, sendJsonMessage);

  const handleUpdateStart = useCallback(() => {
    setIsUpdating(true);
  }, []);

  const handleUpdateComplete = useCallback(() => {
    setIsUpdating(false);
  }, []);

  const handleDeleteNode = useCallback(
    async (nodeId) => {
      setIsDeleting(true);
      try {
        // Canvas onDelete is bound to this node; keep using latest from props.
        await data.onDelete(nodeId);
      } catch (error) {
        console.error('Error deleting node:', error);
        setIsDeleting(false);
        throw error;
      }
    },
    [data]
  );

  return (
    <NodeContainer isTemp={isTemp}>
      {isTemp ? (
        <Typography variant="body2" color="textSecondary">
          Creating {nodeData.label}...
        </Typography>
      ) : (
        <>
          <NodeHeader
            data={{
              ...nodeData,
              isUpdating,
              isDeleting,
            }}
            projectId={projectId}
            updateNodeData={updateNodeData}
            onOpenModal={data.onOpenModal}
            onDeleteNode={handleDeleteNode}
            handleDeploy={handleDeploy}
            handleDestroy={handleDestroy}
            handleRun={handleRun}
            handleRunNow={handleRunNow}
            handleActivate={handleActivate}
            handleDeactivate={handleDeactivate}
            hasAgentGraph={hasAgentGraph}
            destroyBlockedByDownstream={destroyBlockedByDownstream}
            edges={canvasEdges}
            onUpdateStart={handleUpdateStart}
            onUpdateComplete={handleUpdateComplete}
          />
          <StatusContainer
            sx={{
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                maxWidth: 240,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {typeLine}
              </Typography>
              {tokenCount != null && (
                <Chip
                  size="small"
                  label={`${tokenCount} tok`}
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              )}
              {nodeData.type === 'workflow/timer' && (
                <Chip
                  size="small"
                  label={timerArmed ? 'armed' : 'idle'}
                  color={timerArmed ? 'success' : 'default'}
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              )}
              {nodeData.type === 'workflow/pause' && (
                <Chip
                  size="small"
                  label={nodeData.parameter_data?.paused ? 'paused' : 'open'}
                  color={nodeData.parameter_data?.paused ? 'warning' : 'success'}
                  onClick={async () => {
                    const nextPaused = !nodeData.parameter_data?.paused;
                    updateNodeData({
                      parameter_data: {
                        ...nodeData.parameter_data,
                        paused: nextPaused,
                      },
                    });
                    try {
                      await api.updatePackage(projectId, id, {
                        parameter_data: {
                          ...nodeData.parameter_data,
                          paused: nextPaused,
                        },
                      });
                    } catch {
                      /* node keeps optimistic state; server truth arrives via WS */
                    }
                  }}
                  sx={{ height: 18, fontSize: '0.65rem', cursor: 'pointer' }}
                />
              )}
              {lastRunLabel ? (
                <Chip
                  size="small"
                  label={lastRunLabel}
                  onClick={openJobs}
                  color={
                    nodeData.output_data?.last_run?.status === 'ok'
                      ? 'success'
                      : nodeData.output_data?.last_run?.status === 'running'
                        ? 'info'
                        : 'error'
                  }
                  sx={{ height: 18, fontSize: '0.65rem', cursor: 'pointer' }}
                />
              ) : null}
            </Box>
            <Popover
              open={jobsOpen}
              anchorEl={jobsAnchor}
              onClose={closeJobs}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Box sx={{ p: 1.5, maxWidth: 320 }}>
                <Typography variant="caption" color="text.secondary">
                  Recent runs
                </Typography>
                {nodeJobs.length === 0 ? (
                  <Typography variant="body2">No runs yet</Typography>
                ) : (
                  <List dense disablePadding>
                    {nodeJobs.slice(0, 8).map((j) => {
                      const raw = j.raw || j;
                      const res = (() => {
                        try {
                          return typeof raw.result === 'string'
                            ? JSON.parse(raw.result)
                            : raw.result || {};
                        } catch {
                          return {};
                        }
                      })();
                      const stdout =
                        (typeof res.stdout === 'string' && res.stdout) ||
                        '';
                      return (
                        <ListItem key={j.id || raw.id} disableGutters>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption">
                              {(raw.status || j.status || '?')} ·{' '}
                              {String(
                                raw.updated_at ||
                                  raw.created_at ||
                                  j.updated_at ||
                                  ''
                              ).slice(0, 19).replace('T', ' ')}
                              {res.exit != null ? ` · exit ${res.exit}` : ''}
                            </Typography>
                            {stdout ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', whiteSpace: 'pre-wrap' }}
                                noWrap
                              >
                                {stdout.slice(0, 140)}
                              </Typography>
                            ) : null}
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>
            </Popover>
            {handlePreview ? (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                title={handlePreview}
                sx={{ maxWidth: 240 }}
              >
                {handlePreview}
              </Typography>
            ) : null}
          </StatusContainer>
          <NodeHandles
            nodeId={id}
            inputs={nodeData.inputs}
            outputs={nodeData.outputs}
            isConnectable={isConnectable}
            isTemp={isTemp}
            packageType={nodeData.type}
            vertical={Boolean(data.flowVertical)}
          />
          {error && <Typography color="error">{error}</Typography>}
        </>
      )}
    </NodeContainer>
  );
});

export default ProjectPackage;
